/**
 * Payroll Helper — pure orchestration utilities for the Payroll Run.
 *
 * Keeps the form lean by isolating:
 *   - Per-line salary computation (proration based on attendance)
 *   - Recovery distribution (FIFO across multiple ADV/LON docs per staff)
 *   - Recovery side-effect application + reversal on Posted/Cancelled/Edit
 */

import {
  collection, doc, getDocs, getDoc, query, where, writeBatch, updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { voucherRecoveriesFor } from './loanRecoveryHelper';

// ────────────────────────────────────────────────────────────────────
// 1. Per-line salary computation
// ────────────────────────────────────────────────────────────────────

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Auto-overtime amount from hours worked (rate derived from salary):
 *   per-hour rate = Basic ÷ Working Days ÷ Hours-per-day
 *   OT amount     = OT hours × per-hour rate × multiplier
 * Returns 0 when hours/days/hoursPerDay are not positive, so a blank OT-hours
 * field never disturbs a manually typed overtime amount.
 */
export const computeOtAmount = (basicSalary, totalWorkingDays, hoursPerDay, otHours, multiplier) => {
  const days = Number(totalWorkingDays) || 0;
  const hpd = Number(hoursPerDay) || 0;
  const hrs = Number(otHours) || 0;
  const mult = Number(multiplier) || 0;
  if (days <= 0 || hpd <= 0 || hrs <= 0) return 0;
  const perHour = (Number(basicSalary) || 0) / days / hpd;
  return r2(hrs * perHour * mult);
};

/**
 * Fixed-rate overtime: OT amount = OT hours × the staff's fixed OT rate/hour.
 * Used when a payroll run's OT method is 'fixed'. No multiplier — the staff's
 * rate IS the final per-hour overtime pay. Returns 0 when hours or rate ≤ 0.
 */
export const computeOtFixed = (otHours, otRatePerHour) => {
  const hrs = Number(otHours) || 0;
  const rate = Number(otRatePerHour) || 0;
  if (hrs <= 0 || rate <= 0) return 0;
  return r2(hrs * rate);
};

/**
 * Build a payroll line for a single staff member from their salary structure
 * + attendance + any auto-pulled advance/loan recoveries.
 */
export const buildPayrollLine = (staff, attendance, recoveries) => {
  const totalWorkingDays = Number(attendance.totalWorkingDays) || 26;
  const present = Number(attendance.present) || 0;
  const absent = Number(attendance.absent) || 0;
  const paidLeave = Number(attendance.paidLeave) || 0;
  const payableDays = present + paidLeave;
  const proration = totalWorkingDays > 0 ? payableDays / totalWorkingDays : 0;

  const basicSalary = Number(staff.basicSalary) || 0;
  const basicProrated = r2(basicSalary * proration);

  // Allowances — each prorated on its own
  const allowances = (staff.earnings || []).map(e => {
    const fullValue = e.amountType === 'percent'
      ? basicSalary * (Number(e.value) || 0) / 100
      : Number(e.value) || 0;
    return {
      name: e.name,
      amountType: e.amountType,
      value: Number(e.value) || 0,
      fullValue: r2(fullValue),
      prorated: r2(fullValue * proration)
    };
  });
  const allowancesTotal = allowances.reduce((s, a) => s + a.prorated, 0);
  const effectiveGross = r2(basicProrated + allowancesTotal);

  // Adjustments default to zero
  const bonus = Number(attendance.bonus) || 0;
  const arrears = Number(attendance.arrears) || 0;
  const otHours = Number(attendance.otHours) || 0;
  const overtime = Number(attendance.overtime) || 0;
  const leaveDeduction = Number(attendance.leaveDeduction) || 0;
  const adjustedGross = r2(effectiveGross + bonus + arrears + overtime - leaveDeduction);

  // Deductions — statutory not prorated by default (real-world: EOBI is fixed monthly)
  // User can edit per row if they want to prorate.
  const deductions = (staff.deductions || []).map(d => {
    const computed = d.amountType === 'percent'
      ? basicSalary * (Number(d.value) || 0) / 100
      : Number(d.value) || 0;
    return {
      name: d.name,
      amountType: d.amountType,
      value: Number(d.value) || 0,
      accountId: d.accountId,
      accountCode: d.accountCode,
      accountName: d.accountName,
      computed: r2(computed)
    };
  });
  const statutoryTotal = deductions.reduce((s, d) => s + d.computed, 0);

  const advanceRecovery = recoveries?.advanceTotal || 0;
  const advanceRefs = recoveries?.advanceRefs || [];
  const loanInstallment = recoveries?.loanTotal || 0;
  const loanRefs = recoveries?.loanRefs || [];

  const totalDeductions = r2(statutoryTotal + advanceRecovery + loanInstallment);
  const netPayable = r2(adjustedGross - totalDeductions);

  return {
    staffId: staff.id,
    staffCode: staff.staffCode,
    staffName: staff.fullName,
    staffAccountId: staff.linkedAccountId,
    staffAccountCode: staff.linkedAccountCode || '',
    designation: staff.designation,
    department: staff.department,

    // Attendance
    totalWorkingDays, present, absent, paidLeave, payableDays,

    // Earnings
    basicSalary, basicProrated,
    allowances, allowancesTotal: r2(allowancesTotal),
    effectiveGross,

    // Adjustments
    bonus, arrears, otHours, overtime, leaveDeduction,
    otRatePerHour: Number(staff.otRatePerHour) || 0, // for the run's Fixed Per-Hour OT method
    adjustedGross,

    // Deductions
    deductions, statutoryTotal: r2(statutoryTotal),
    advanceRecovery: r2(advanceRecovery), advanceRefs,
    loanInstallment: r2(loanInstallment), loanRefs,
    totalDeductions, netPayable,

    // Pay status
    paid: false, paidAt: null
  };
};

/**
 * Recompute a payroll line after the user edits attendance / adjustments / recoveries.
 * Preserves the staff identity + structure snapshot but recalculates derived totals.
 */
export const recomputeLine = (line) => {
  const totalWorkingDays = Number(line.totalWorkingDays) || 26;
  const payableDays = (Number(line.present) || 0) + (Number(line.paidLeave) || 0);
  const proration = totalWorkingDays > 0 ? payableDays / totalWorkingDays : 0;

  const basicProrated = r2((Number(line.basicSalary) || 0) * proration);
  const allowances = (line.allowances || []).map(a => ({
    ...a,
    prorated: r2((Number(a.fullValue) || 0) * proration)
  }));
  const allowancesTotal = allowances.reduce((s, a) => s + a.prorated, 0);
  const effectiveGross = r2(basicProrated + allowancesTotal);

  const adjustedGross = r2(
    effectiveGross
    + (Number(line.bonus) || 0)
    + (Number(line.arrears) || 0)
    + (Number(line.overtime) || 0)
    - (Number(line.leaveDeduction) || 0)
  );

  const statutoryTotal = (line.deductions || []).reduce((s, d) => s + (Number(d.computed) || 0), 0);
  const totalDeductions = r2(
    statutoryTotal
    + (Number(line.advanceRecovery) || 0)
    + (Number(line.loanInstallment) || 0)
  );
  const netPayable = r2(adjustedGross - totalDeductions);

  return {
    ...line,
    payableDays,
    basicProrated,
    allowances,
    allowancesTotal: r2(allowancesTotal),
    effectiveGross,
    adjustedGross,
    statutoryTotal: r2(statutoryTotal),
    totalDeductions,
    netPayable
  };
};

// ────────────────────────────────────────────────────────────────────
// 2. Recovery preparation — find outstanding ADV/LON docs per staff
// ────────────────────────────────────────────────────────────────────

/**
 * Load outstanding advances & active loan installments for a list of staff.
 * Pre-fills the recovery amounts (advance: full outstanding, loan: monthly installment
 * if firstInstallmentMonth ≤ payrollMonth).
 *
 * Returns map: { [staffId]: { advanceTotal, advanceRefs, loanTotal, loanRefs } }
 */
export const loadRecoveriesForStaff = async (companyId, staffIds, payrollMonth) => {
  const result = {};
  staffIds.forEach(id => { result[id] = { advanceTotal: 0, advanceRefs: [], loanTotal: 0, loanRefs: [] }; });

  // Load all Issued advances with outstanding > 0
  const advSnap = await getDocs(query(
    collection(db, 'companies', companyId, 'transactions'),
    where('tranTypeCode', '==', 'ADV'),
    where('status', '==', 'Issued')
  ));
  advSnap.docs.forEach(d => {
    const data = d.data();
    if (!staffIds.includes(data.partyId)) return;
    const outstanding = Number(data.outstandingAmount ?? data.totalAmount) || 0;
    if (outstanding <= 0) return;
    result[data.partyId].advanceTotal = r2(result[data.partyId].advanceTotal + outstanding);
    result[data.partyId].advanceRefs.push({
      transactionId: d.id,
      docNumber: data.docNumber,
      amount: r2(outstanding)
    });
  });

  // Load all Active loans whose firstInstallmentMonth ≤ payrollMonth
  const lonSnap = await getDocs(query(
    collection(db, 'companies', companyId, 'transactions'),
    where('tranTypeCode', '==', 'LON'),
    where('status', '==', 'Active')
  ));
  lonSnap.docs.forEach(d => {
    const data = d.data();
    if (!staffIds.includes(data.partyId)) return;
    if ((data.firstInstallmentMonth || '') > payrollMonth) return; // not yet started
    const outstanding = Number(data.outstandingAmount ?? data.totalAmount) || 0;
    if (outstanding <= 0) return;
    // Use either monthly installment or last-installment if it's the final one
    const paid = Number(data.paidInstallments) || 0;
    const totalInst = Number(data.numberOfInstallments) || 1;
    const isLast = paid === totalInst - 1;
    const installment = r2(Math.min(
      isLast ? (Number(data.lastInstallment) || data.monthlyInstallment) : (Number(data.monthlyInstallment) || 0),
      outstanding
    ));
    if (installment <= 0) return;
    result[data.partyId].loanTotal = r2(result[data.partyId].loanTotal + installment);
    result[data.partyId].loanRefs.push({
      transactionId: d.id,
      docNumber: data.docNumber,
      amount: installment
    });
  });

  return result;
};

// ────────────────────────────────────────────────────────────────────
// 3. Apply / Reverse recovery side-effects
// ────────────────────────────────────────────────────────────────────

/**
 * After Posting a payroll, distribute each line's advance/loan recovery across
 * the underlying ADV/LON docs (FIFO: oldest first), updating their
 * recoveredAmount, outstandingAmount, paidInstallments, and status.
 */
export const applyRecoveries = async (companyId, payrollLines, payrollTransactionId) => {
  const batch = writeBatch(db);
  const updates = [];

  for (const line of (payrollLines || [])) {
    // Distribute advance recovery FIFO across line.advanceRefs
    let remaining = Number(line.advanceRecovery) || 0;
    for (const ref of (line.advanceRefs || [])) {
      if (remaining <= 0) break;
      const txRef = doc(db, 'companies', companyId, 'transactions', ref.transactionId);
      const snap = await getDoc(txRef);
      if (!snap.exists()) continue;
      const advData = snap.data();
      const currentOutstanding = Number(advData.outstandingAmount ?? advData.totalAmount) || 0;
      const recoverHere = Math.min(remaining, currentOutstanding);
      if (recoverHere <= 0) continue;
      const newRecovered = r2((Number(advData.recoveredAmount) || 0) + recoverHere);
      const newOutstanding = r2((Number(advData.totalAmount) || 0) - newRecovered);
      const newStatus = newOutstanding <= 0.01 ? 'Recovered' : advData.status;
      updates.push({ ref: txRef, data: {
        recoveredAmount: newRecovered,
        outstandingAmount: Math.max(0, newOutstanding),
        status: newStatus,
        lastRecoveryPayrollId: payrollTransactionId
      }});
      remaining = r2(remaining - recoverHere);
    }

    // Distribute loan installment FIFO across line.loanRefs
    remaining = Number(line.loanInstallment) || 0;
    for (const ref of (line.loanRefs || [])) {
      if (remaining <= 0) break;
      const txRef = doc(db, 'companies', companyId, 'transactions', ref.transactionId);
      const snap = await getDoc(txRef);
      if (!snap.exists()) continue;
      const lonData = snap.data();
      const currentOutstanding = Number(lonData.outstandingAmount ?? lonData.totalAmount) || 0;
      const recoverHere = Math.min(remaining, currentOutstanding);
      if (recoverHere <= 0) continue;
      const newRecovered = r2((Number(lonData.recoveredAmount) || 0) + recoverHere);
      const newOutstanding = r2((Number(lonData.totalAmount) || 0) - newRecovered);
      const newPaidInst = (Number(lonData.paidInstallments) || 0) + 1;
      const newStatus = newOutstanding <= 0.01 ? 'Closed' : lonData.status;
      updates.push({ ref: txRef, data: {
        recoveredAmount: newRecovered,
        outstandingAmount: Math.max(0, newOutstanding),
        paidInstallments: newPaidInst,
        status: newStatus,
        lastRecoveryPayrollId: payrollTransactionId
      }});
      remaining = r2(remaining - recoverHere);
    }
  }

  updates.forEach(u => batch.update(u.ref, u.data));
  if (updates.length > 0) await batch.commit();
};

/**
 * Reverse the recovery side-effects when a Posted payroll is cancelled or
 * re-saved. Subtracts the recovered amounts back, decrements paidInstallments,
 * and reverts status from Recovered/Closed back to Issued/Active if needed.
 */
export const reverseRecoveries = async (companyId, payrollLines) => {
  const batch = writeBatch(db);
  const updates = [];

  for (const line of (payrollLines || [])) {
    // Reverse advance recoveries — clamp to currently-recovered amount to avoid double-reverse making state negative
    let remaining = Number(line.advanceRecovery) || 0;
    for (const ref of (line.advanceRefs || [])) {
      if (remaining <= 0) break;
      const txRef = doc(db, 'companies', companyId, 'transactions', ref.transactionId);
      const snap = await getDoc(txRef);
      if (!snap.exists()) continue;
      const advData = snap.data();
      const alreadyRecovered = Math.max(0, Number(advData.recoveredAmount) || 0);
      // Use the smaller of: remaining-to-reverse, the slice originally taken, actual currently-recovered
      const originalSlice = Math.max(0, Number(ref.amount) || remaining);
      const reverseHere = Math.min(remaining, originalSlice, alreadyRecovered);
      if (reverseHere <= 0) { remaining = r2(remaining - originalSlice); continue; }
      const newRecovered = r2(alreadyRecovered - reverseHere);
      const newOutstanding = r2((Number(advData.totalAmount) || 0) - newRecovered);
      const newStatus = advData.status === 'Recovered' && newOutstanding > 0.01 ? 'Issued' : advData.status;
      updates.push({ ref: txRef, data: {
        recoveredAmount: Math.max(0, newRecovered),
        outstandingAmount: Math.max(0, newOutstanding),
        status: newStatus
      }});
      remaining = r2(remaining - reverseHere);
    }

    // Reverse loan recoveries — same clamp
    remaining = Number(line.loanInstallment) || 0;
    for (const ref of (line.loanRefs || [])) {
      if (remaining <= 0) break;
      const txRef = doc(db, 'companies', companyId, 'transactions', ref.transactionId);
      const snap = await getDoc(txRef);
      if (!snap.exists()) continue;
      const lonData = snap.data();
      const alreadyRecovered = Math.max(0, Number(lonData.recoveredAmount) || 0);
      const originalSlice = Math.max(0, Number(ref.amount) || remaining);
      const reverseHere = Math.min(remaining, originalSlice, alreadyRecovered);
      if (reverseHere <= 0) { remaining = r2(remaining - originalSlice); continue; }
      const newRecovered = r2(alreadyRecovered - reverseHere);
      const newOutstanding = r2((Number(lonData.totalAmount) || 0) - newRecovered);
      const newPaidInst = Math.max(0, (Number(lonData.paidInstallments) || 0) - 1);
      const newStatus = lonData.status === 'Closed' && newOutstanding > 0.01 ? 'Active' : lonData.status;
      updates.push({ ref: txRef, data: {
        recoveredAmount: Math.max(0, newRecovered),
        outstandingAmount: Math.max(0, newOutstanding),
        paidInstallments: newPaidInst,
        status: newStatus
      }});
      remaining = r2(remaining - reverseHere);
    }
  }

  updates.forEach(u => batch.update(u.ref, u.data));
  if (updates.length > 0) await batch.commit();
};

/**
 * Self-heal a stuck ADV/LON: recompute its recovery state from the payrolls that
 * ACTUALLY still exist (non-cancelled) and reference it. If none reference it —
 * e.g. those payrolls were deleted — recovered drops to 0 and the doc unlocks.
 * Repairs records left inconsistent by an earlier failed/partial payroll delete.
 * Only touches TRACKING fields (recovered/outstanding/installments/status); the
 * ledger is already correct (the payroll's own entries were removed on delete).
 */
export const recomputeRecoveryFromLivePayrolls = async (companyId, tx) => {
  const isLoan = tx.tranTypeCode === 'LON';
  const paySnap = await getDocs(query(
    collection(db, 'companies', companyId, 'transactions'),
    where('tranTypeCode', '==', 'PAY')
  ));
  let recovered = 0;
  let installments = 0;
  paySnap.docs.forEach(d => {
    const p = d.data();
    if (p.status === 'Cancelled') return;
    (p.payrollLines || []).forEach(line => {
      const refs = isLoan ? (line.loanRefs || []) : (line.advanceRefs || []);
      refs.forEach(ref => {
        if (ref && ref.transactionId === tx.id) {
          const amt = Number(ref.amount) || 0;
          if (amt > 0) { recovered += amt; if (isLoan) installments += 1; }
        }
      });
    });
  });
  // Posted Loan Recovery vouchers (paid back in cash / bank) count too — else
  // this repair would wipe them out.
  const byVoucher = await voucherRecoveriesFor(companyId, tx.id);
  recovered = r2(recovered + byVoucher.amount);
  const total = Number(tx.totalAmount) || 0;
  const outstanding = Math.max(0, r2(total - recovered));
  const update = { recoveredAmount: recovered, outstandingAmount: outstanding };
  if (byVoucher.amount > 0 || tx.voucherRecoveredAmount != null) update.voucherRecoveredAmount = byVoucher.amount;
  if (isLoan) {
    update.paidInstallments = installments;
    update.status = outstanding <= 0.01 ? 'Closed' : 'Active';
  } else {
    update.status = outstanding <= 0.01 ? 'Recovered' : 'Issued';
  }
  await updateDoc(doc(db, 'companies', companyId, 'transactions', tx.id), update);
  return { recovered, outstanding };
};

/**
 * For Final Settlement: load FULL outstanding (not just monthly installment) for one staff.
 * Returns { advanceTotal, advanceRefs, loanTotal, loanRefs }.
 */
export const loadFullOutstandingForStaff = async (companyId, staffId) => {
  const result = { advanceTotal: 0, advanceRefs: [], loanTotal: 0, loanRefs: [] };
  if (!staffId) return result;

  const advSnap = await getDocs(query(
    collection(db, 'companies', companyId, 'transactions'),
    where('tranTypeCode', '==', 'ADV'),
    where('partyId', '==', staffId),
    where('status', '==', 'Issued')
  ));
  advSnap.docs.forEach(d => {
    const data = d.data();
    const outstanding = Number(data.outstandingAmount ?? data.totalAmount) || 0;
    if (outstanding <= 0) return;
    result.advanceTotal = r2(result.advanceTotal + outstanding);
    result.advanceRefs.push({ transactionId: d.id, docNumber: data.docNumber, amount: r2(outstanding) });
  });

  const lonSnap = await getDocs(query(
    collection(db, 'companies', companyId, 'transactions'),
    where('tranTypeCode', '==', 'LON'),
    where('partyId', '==', staffId),
    where('status', '==', 'Active')
  ));
  lonSnap.docs.forEach(d => {
    const data = d.data();
    const outstanding = Number(data.outstandingAmount ?? data.totalAmount) || 0;
    if (outstanding <= 0) return;
    result.loanTotal = r2(result.loanTotal + outstanding);
    result.loanRefs.push({ transactionId: d.id, docNumber: data.docNumber, amount: r2(outstanding) });
  });

  return result;
};

/**
 * F&F variant: like applyRecoveries but works with the F&F line format
 * (loan recovery may settle multiple loans fully, not just one installment).
 * Also marks the staff as inactive on Post.
 */
export const applyFinalSettlementSideEffects = async (companyId, fnfData, transactionId) => {
  // Atomic: build recovery updates + staff deactivation in a single batch so a mid-operation
  // crash cannot leave recoveries applied but staff still active (or vice versa).
  const batch = writeBatch(db);

  // Advance recoveries (FIFO)
  let remaining = Number(fnfData.advanceRecovery) || 0;
  for (const ref of (fnfData.advanceRefs || [])) {
    if (remaining <= 0) break;
    const txRef = doc(db, 'companies', companyId, 'transactions', ref.transactionId);
    const snap = await getDoc(txRef);
    if (!snap.exists()) continue;
    const advData = snap.data();
    const currentOutstanding = Number(advData.outstandingAmount ?? advData.totalAmount) || 0;
    const recoverHere = Math.min(remaining, currentOutstanding);
    if (recoverHere <= 0) continue;
    const newRecovered = r2((Number(advData.recoveredAmount) || 0) + recoverHere);
    const newOutstanding = r2((Number(advData.totalAmount) || 0) - newRecovered);
    const newStatus = newOutstanding <= 0.01 ? 'Recovered' : advData.status;
    batch.update(txRef, {
      recoveredAmount: newRecovered,
      outstandingAmount: Math.max(0, newOutstanding),
      status: newStatus,
      lastRecoveryPayrollId: transactionId
    });
    remaining = r2(remaining - recoverHere);
  }

  // Loan recoveries (FIFO) — F&F settles full outstanding, not installment
  remaining = Number(fnfData.loanRecovery) || 0;
  for (const ref of (fnfData.loanRefs || [])) {
    if (remaining <= 0) break;
    const txRef = doc(db, 'companies', companyId, 'transactions', ref.transactionId);
    const snap = await getDoc(txRef);
    if (!snap.exists()) continue;
    const lonData = snap.data();
    const currentOutstanding = Number(lonData.outstandingAmount ?? lonData.totalAmount) || 0;
    const recoverHere = Math.min(remaining, currentOutstanding);
    if (recoverHere <= 0) continue;
    const newRecovered = r2((Number(lonData.recoveredAmount) || 0) + recoverHere);
    const newOutstanding = r2((Number(lonData.totalAmount) || 0) - newRecovered);
    const newStatus = newOutstanding <= 0.01 ? 'Closed' : lonData.status;
    batch.update(txRef, {
      recoveredAmount: newRecovered,
      outstandingAmount: Math.max(0, newOutstanding),
      status: newStatus,
      lastRecoveryPayrollId: transactionId
    });
    remaining = r2(remaining - recoverHere);
  }

  // Mark staff as inactive (preserves history; blocks new payroll) — same batch
  if (fnfData.staffId) {
    const staffRef = doc(db, 'companies', companyId, 'salaryStaff', fnfData.staffId);
    batch.update(staffRef, {
      isActive: false,
      lastWorkingDay: fnfData.lastWorkingDay || null,
      leavingReason: fnfData.leavingReason || null,
      finalSettlementId: transactionId,
      updatedAt: new Date().toISOString()
    });
  }

  await batch.commit();
};

/**
 * F&F variant of reverseRecoveries: also re-activates the staff.
 */
export const reverseFinalSettlementSideEffects = async (companyId, fnfData) => {
  await reverseRecoveries(companyId, [{
    advanceRecovery: fnfData.advanceRecovery || 0,
    advanceRefs: fnfData.advanceRefs || [],
    loanInstallment: fnfData.loanRecovery || 0,
    loanRefs: fnfData.loanRefs || []
  }]);

  if (fnfData.staffId) {
    const staffRef = doc(db, 'companies', companyId, 'salaryStaff', fnfData.staffId);
    await updateDoc(staffRef, {
      isActive: true,
      finalSettlementId: null,
      updatedAt: new Date().toISOString()
    });
  }
};

const payrollHelper = {
  buildPayrollLine,
  recomputeLine,
  loadRecoveriesForStaff,
  applyRecoveries,
  reverseRecoveries,
  loadFullOutstandingForStaff,
  applyFinalSettlementSideEffects,
  reverseFinalSettlementSideEffects
};

export default payrollHelper;
